import { useEffect } from "react";
import { Link } from "react-router-dom";
import { OdingardMark } from "../brand/OdingardLogo";

const ERA = [
  {
    n: "01",
    title: "Agents act, not just answer",
    body: "Autonomous AI now calls tools, moves data and commits transactions with no human in the loop. Every one of those actions is a security decision.",
  },
  {
    n: "02",
    title: "Old controls watch the wrong thing",
    body: "Filters judge what a model says. The risk is in what it does. Security for this era has to sit where the action happens.",
  },
  {
    n: "03",
    title: "Trust now requires proof",
    body: "Boards, auditors and regulators ask who allowed an AI decision and what evidence shows it. A policy on paper is no longer an answer.",
  },
];

const PRINCIPLES = [
  {
    title: "Enforce the action",
    body: "Authority is checked at the moment an AI system acts, not reconstructed after the fact.",
  },
  {
    title: "Prove the outcome",
    body: "Every decision leaves evidence that can be verified independently and cannot be rewritten.",
  },
  {
    title: "Publish the research",
    body: "Our methods are public, peer-reviewable and carry a DOI, so customers can check the work.",
  },
];

const DISCIPLINES = [
  {
    eyebrow: "Discipline 01",
    name: "AI Security",
    body: "Protecting enterprises from AI systems that act on their own: runtime defense, adversarial testing and threat detection for autonomous agents.",
    href: "/ai-security",
  },
  {
    eyebrow: "Discipline 02",
    name: "Governance & Compliance",
    body: "Keeping AI and the business around it continuously compliant, with evidence an auditor can verify instead of a scramble once a year.",
    href: "/governance",
  },
];

const RESEARCH = [
  {
    theme: "Evidence",
    title: "Source independence as a measurable property",
  },
  {
    theme: "Containment",
    title: "Containment survivability under attack",
  },
  {
    theme: "Autonomy",
    title: "Runtime execution control for agents",
  },
];

export function Home() {
  useEffect(() => {
    document.title = "Odingard Security — The leader in AI security and compliance";
  }, []);

  return (
    <main className="page home" id="main">
      <section className="hx">
        <div className="hx__shafts" aria-hidden="true">
          {Array.from({ length: 9 }, (_, i) => (
            <span key={i} className="hx__shaft" />
          ))}
          <span className="hx__sweep" />
        </div>
        <div className="hx__copy">
          <p className="hx__eyebrow">
            <OdingardMark className="hx__mark" />
            Odingard Security
          </p>
          <h1 className="hx__title">The leader in AI security and compliance</h1>
          <p className="hx__lede">
            AI now acts on its own. Odingard is the company built for that era:
            we keep autonomous systems inside their authority, and we prove it.
          </p>
          <div className="hx__cta">
            <Link to="/contact?topic=briefing" className="sq sq--light">
              Request a briefing
            </Link>
            <Link to="/company" className="sq sq--ghost">
              About Odingard
            </Link>
          </div>
        </div>
      </section>

      <div className="frame">
        <section className="sec sec--center" aria-labelledby="who">
          <p className="sec__eyebrow">Who we are</p>
          <h2 id="who" className="serif sec__title">
            A security company for AI that acts
          </h2>
          <p className="sec__lede">
            Odingard Security builds the controls and the evidence that let
            enterprises put autonomous AI to work safely. We started from
            research, not a product roadmap, and we still publish the work
            everything we build stands on.
          </p>
          <Link to="/company" className="sec__link">
            About us →
          </Link>
        </section>

        <section className="sec" aria-labelledby="era">
          <p className="sec__eyebrow sec__eyebrow--center">The agentic era</p>
          <h2 id="era" className="serif sec__title sec__title--center">
            Software that decides changes what security means
          </h2>
          <div className="era">
            {ERA.map((e) => (
              <article key={e.n} className="era__card">
                <span className="era__n">{e.n}</span>
                <h3>{e.title}</h3>
                <p>{e.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="prin" aria-labelledby="principles">
          <div className="prin__glow" aria-hidden="true" />
          <p className="sec__eyebrow sec__eyebrow--center prin__eyebrow">
            How we work
          </p>
          <h2 id="principles" className="serif prin__title">
            Three principles, no exceptions
          </h2>
          <div className="prin__grid">
            {PRINCIPLES.map((p) => (
              <div key={p.title} className="prin__item">
                <OdingardMark className="prin__mark" />
                <h3>{p.title}</h3>
                <p>{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="sec" aria-labelledby="disciplines">
          <p className="sec__eyebrow sec__eyebrow--center">What we do</p>
          <h2 id="disciplines" className="serif sec__title sec__title--center">
            Two disciplines, one company
          </h2>
          <div className="disc">
            {DISCIPLINES.map((d) => (
              <Link key={d.href} to={d.href} className="disc__card">
                <span className="disc__eyebrow">{d.eyebrow}</span>
                <span className="serif disc__name">{d.name}</span>
                <span className="disc__body">{d.body}</span>
                <span className="disc__more">Explore {d.name} →</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="sec" aria-labelledby="research">
          <p className="sec__eyebrow sec__eyebrow--center">Research</p>
          <h2 id="research" className="serif sec__title sec__title--center">
            Built on published research
          </h2>
          <p className="sec__lede sec__lede--center">
            Every paper carries a DOI, so you can read the argument, check the
            method and disagree with it in public.
          </p>
          <div className="res">
            {RESEARCH.map((r) => (
              <Link key={r.title} to="/research" className="res__card">
                <span className="res__theme">{r.theme}</span>
                <span className="res__title">{r.title}</span>
                <span className="res__more">Read the research →</span>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <section className="hx hx--close" aria-labelledby="close">
        <div className="hx__shafts" aria-hidden="true">
          {Array.from({ length: 9 }, (_, i) => (
            <span key={i} className="hx__shaft" />
          ))}
          <span className="hx__sweep" />
        </div>
        <div className="hx__copy hx__copy--left">
          <p className="sec__eyebrow hx__eyebrow-plain">Get in touch</p>
          <h2 id="close" className="hx__title hx__title--close">
            Secure the action. Prove the outcome.
          </h2>
          <p className="hx__lede">
            Talk to the team about putting autonomous AI to work without losing
            control of it.
          </p>
          <div className="hx__cta hx__cta--left">
            <Link to="/contact?topic=briefing" className="sq sq--light">
              Request a briefing
            </Link>
            <Link to="/contact" className="sq sq--ghost">
              Contact us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
